import { httpsCallable } from 'firebase/functions';
import { functions } from 'src/firebase/firebaseConfig';
import { queryClient } from 'src/utils/queryClient';
import { LoaderFunctionArgs } from 'react-router';
import { DayliteQueryOptions, DayliteFilterOperand } from 'shared/types/dayliteModels';

export interface OpportunityQueryData {
  self: string;
  name: string;
  forecasted?: string;
  state?: string;
  category?: string;
  probability?: number;
  value?: number;
  owner?: string;
}

interface QueryEventsResponse {
  success: boolean;
  data?: {
    results: OpportunityQueryData[];
    next?: string;
  };
  message?: string;
}

/**
 * Call the Firebase function to search for events using Daylite API
 * @param options Query parameters with filters, sorting, limit, etc.
 */
const searchEvents = async (options: DayliteQueryOptions = {}): Promise<QueryEventsResponse> => {
  try {
    // Pass the options directly to the function without modification
    const searchEventsFunction = httpsCallable<DayliteQueryOptions, QueryEventsResponse>(functions, 'searchEvents');

    const result = await searchEventsFunction(options);

    // Ensure we always have a valid response structure
    return (
      result.data || {
        success: false,
        message: 'No data returned',
        data: { results: [], next: undefined }
      }
    );
  } catch (err) {
    console.error('Error querying events:', err);
    return {
      success: false,
      message: 'Failed to query events',
      data: { results: [], next: undefined }
    };
  }
};

/**
 * Format date for Daylite API in YYYY-MM-DD format
 */
function formatDateForDaylite(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * React Router loader function for fetching all events
 * NOTE: This loader is being phased out in favor of component-level state management.
 * The EventFilterControls component now handles search with preserved state.
 * This loader returns null to avoid conflicts with the component's search logic.
 */
export async function allEventsLoader() {
  // Return null - let the component handle loading with its preserved state
  return {
    events: null
  };
}

export { searchEvents };
